import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from '@mui/material/Box';
import { useDispatch } from "react-redux";
import ControlsBar from "@/components/builder/ControlBar";
import Options_bar from "@/components/builder/OptionBar";
import Room_view from "@/components/builder/RoomViewOld";

import { BuilderState, actions as builderActions } from "@/store/builderSlice";
import RoomView from "@/components/builder/RoomView";
import { useRouter } from "next/router";
import { usePathname, useSearchParams } from "next/navigation";
import { enablePatches } from "immer";
import RoomModelsOverlay from "@/components/builder/RoomModelsOverlay";
import { fetchRoomModels } from "@/store/builder/thunks/roomModelsThunk";
import { getBuilderProductData, getBuilderProductTypeData } from "@/store/builder/thunks/builderProductsLoader";
import useProjectLoader from "@/hooks/builder/useProjectLoader";
import ControlPanel from "@/components/builder/ControlPanel";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import SaveNotification from "@/components/builder/SaveNotification";
import { getBuilderDesignData, updateLoadedDesignsMap } from "@/store/builder/thunks/builderDesignsLoader";
import getBuilderContext, { initBuilderContext } from "@/config/getCachedBuilderContext";
import ToolsBar from "@/components/builder/ToolsBar";
import ContextualMenu from "@/components/builder/ContextualMenu";
import { getUserId, setUserId } from "@/utils/builder/userUtils";
import { ParsedUrlQuery } from "querystring";
import { BuilderContext } from "@/config/loadConfig";
import TwoDRoomLayoutBuilder from "./twoDLayout/TwoDRoomLayoutBuilder";
import { getTwoDRoomLayoutVertices } from "@/utils/builder/twoDRoomLayoutUtils";
import RoomLayoutBuilder from "./twoDLayout/RoomLayoutBuilder";
import RoomTypeView from "./RoomTypeView";
import { DimensionalViewType } from "@/utils/builder/builderConstants";
import { getDefaultFloorTexture } from "@/utils/builder/floorTextureUtils";
import BuilderNotification from "./BuilderNotification";
import BuilderSelect from "@/store/builderStoreSelector";
import { Typography ,Button, DialogContent, Dialog, DialogTitle, IconButton, DialogActions, Tooltip, Alert,Menu,MenuItem,Stack ,Chip} from '@mui/material';
import { saveProject } from "@/store/builder/thunks/projectSaveThunk";

export interface BuilderAppProps {
    builderState: BuilderState;
    builderContext: BuilderContext;
    query: ParsedUrlQuery;
}

const BuilderApp: React.FC<BuilderAppProps> = (props: BuilderAppProps) => {
    const dispatch = useDispatch();
    const [reactRouter, setReactRouter] = useState(false);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const previewGenerationList = BuilderSelect.generatePreview().previewGenerationList
    const disableInteraction = !!previewGenerationList.find((eachProject: any) => eachProject.projectId === props?.query?.projectId && (eachProject.previewStatus === 'Initiated' || eachProject.previewStatus === 'Processing'))
    const isHideControlPanelandToolsBar = props?.query?.isPreview || disableInteraction
    const serverState = props.builderState;
    const { projectId, roomType, style, role, userId, createRoom, designId, roomId, showcase, designShowcase, designImage, isPreview, interiorDesigner, layoutId } = props.query;
    const currentRole = (interiorDesigner === 'true' || layoutId) ? 'interiorDesigner' : ''
    const id = currentRole === 'interiorDesigner' ? layoutId : projectId
    const isCreateRoomFlow = createRoom === 'true';
    const isShowcaseFlow = showcase === 'true' && id == null;
    const isProjectLoading = BuilderSelect.ui().isProjectLoading
    const postedThreedGlbImageSignedUrl = BuilderSelect.template().postedThreedGlbImageSignedUrl
    const createNewDesign=BuilderSelect.template().createNewDesign
    enablePatches();
    const project = BuilderSelect.project();
    initBuilderContext(props.builderContext)
    useProjectLoader(id as string, isCreateRoomFlow as boolean, currentRole);

    // Block pointer events to the app when disableInteraction is true, but allow events inside the overlay (menu)
    useEffect(() => {
        if (!disableInteraction) return;

        const handler = (e: Event) => {
            try {
                const target = e.target as Node;
                if (overlayRef.current && overlayRef.current.contains(target)) {
                    // allow interaction inside the overlay (menu)
                    return;
                }
            } catch (err) {
                // ignore
            }
            e.stopImmediatePropagation();
            // preventDefault may be ignored for some events without passive:false, so we set passive below
            if (typeof e.preventDefault === 'function') e.preventDefault();
        };

        const opts = { capture: true, passive: false } as AddEventListenerOptions;
        const events = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'touchstart', 'touchmove', 'touchend', 'mousedown', 'mousemove', 'mouseup'];
        events.forEach(ev => window.addEventListener(ev, handler, opts));
        return () => events.forEach(ev => window.removeEventListener(ev, handler, opts));
    }, [disableInteraction]);
    // to fix: saving sample room and coming back to view it from projects page
    useEffect(() => {
        const { showcase, designShowcase, interiorDesigner } = project?.metaData || {};
        const showcaseFlag = String(showcase).toLowerCase() === 'true';
        const designShowcaseFlag = String(designShowcase).toLowerCase() === 'true';
        const interiorDesignerFlag = String(interiorDesigner).toLowerCase() === 'true';
        const updatedParams = {
            ...props.query,
            ...(showcaseFlag && { showcase: 'true' }),
            ...(designShowcaseFlag && { designShowcase: 'true' }),
            ...(interiorDesignerFlag && { interiorDesigner: 'true', projectId: project?.projectId }),
        };

        dispatch(builderActions.setUrlParams({ urlParams: updatedParams }));
    }, [
        project?.metaData?.showcase,
        project?.metaData?.designShowcase,
        project?.metaData?.interiorDesigner,
        props.query,
    ]);
    useEffect(() => {
        dispatch(builderActions.setUrlParams({
            urlParams:props.query
        }));
        dispatch(builderActions.updateCustomRoomFlow({
            isCustomRoomFlow: isCreateRoomFlow
        }));
        dispatch(builderActions.setProductTypeContent({
            productTypes: serverState.productContent.productTypes
        }));
        dispatch(builderActions.setDesignContent({
            textures: serverState.designContent.textures
        }));
        dispatch(builderActions.addDesigns({
            designData: serverState.designContent.designs
        }));
        dispatch(builderActions.addRoomAssets({
            roomAssets: serverState.roomAssets
        }));
        dispatch(builderActions.addRoomTypes({
            roomTypes: serverState.roomTypes
        }));
        dispatch(builderActions.updateDesignData({
            styleName: style,
            designId: designId
        }));
        dispatch(builderActions.updateProjectMetdata({
            metadata: {
                isInternalUser: window.localStorage.getItem('trafficType') === 'internal', ...(interiorDesigner === 'true' && { interiorDesigner: true })
            }
        }));
        dispatch(builderActions.setViewMode({
            viewMode: isPreview === 'true' ? 'preview' : 'edit'
        }));
        const urlParams = new URLSearchParams(window.location.search);
        const lengthInURL = urlParams.get('l');
        let lengthOfRoom = lengthInURL ? Number(lengthInURL.replace("'", ".")) : 15;
        const widthInURL = urlParams.get('w');
        let widthOfRoom = widthInURL ? Number(widthInURL.replace("'", ".")) : 12;

        const wallColorInURL = urlParams.get('wc');
        let wallColor = wallColorInURL ? "#" + wallColorInURL : "#C2BCb5";
        
        const floorTextureCategoryInURL = urlParams.get('ft');
        let floorTextureCategory = floorTextureCategoryInURL ? floorTextureCategoryInURL : '';
        const floorTextureNameInURL = urlParams.get('ftv');
        let floorTextureName = floorTextureNameInURL ? floorTextureNameInURL : '';

        const foundTexture = serverState.designContent.textures.find(texture => texture.name === floorTextureName && texture.category === floorTextureCategory);
        let foundTextureID = getDefaultFloorTexture()
        if (foundTexture) {
            console.log('Found Texture ID:', foundTexture.id);  // This will print the ID if found
            foundTextureID = foundTexture.id;
        }

        updateLoadedDesignsMap(roomType as string, style as string);
        if (!id) {
            if (isCreateRoomFlow) {
                if (roomId != null)
                    dispatch(fetchRoomModels({ customerId: getUserId(), isCreateRoomFlow, showcase: isShowcaseFlow, roomType: roomType as string }) as any);
                dispatch(builderActions.updateTwoDLayoutVertices({
                    vertices: getTwoDRoomLayoutVertices(lengthOfRoom, widthOfRoom)
                }));
                dispatch(builderActions.updateFloorTexture({
                    floorTextureId: foundTextureID
                }));
                dispatch(builderActions.updateWallColor({
                    color: wallColor,
                    wallId: null
                }));
            } else {
                dispatch(fetchRoomModels({
                    customerId: getUserId(),
                    isCreateRoomFlow,
                    showcase: isShowcaseFlow && roomId == null,
                    roomType: roomType as string,
                    setDefaultRoom: isShowcaseFlow,
                    roomId: roomId as string
                }) as any);
            }
            dispatch(builderActions.updateProjectName({
                projectName: `Project - ${new Date().toLocaleDateString()}`
            }));
            dispatch(builderActions.updateProjectStatus({
                status: 'started'
            }));
        }
        if (isShowcaseFlow) {
            dispatch(builderActions.updateProjectMetdata({
                metadata: {
                    showcase: true
                }
            }));
            //sample room design flow condition to save the metadata
            if (designShowcase === 'true') {
                dispatch(builderActions.updateDesignData({
                    styleName: style,
                    designId: designId,
                }));
                dispatch(builderActions.updateProjectMetdata({
                    metadata: {
                        styleName: style,
                        designId: designId,
                        designImage: decodeURIComponent(designImage as any),
                        designShowcase: true,
                    }
                }));
            }
        }
        setReactRouter(true);
    }, []);

    return (<>
        <div id="builderApp" style={{ position: 'relative' }}>
            <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
                <Box component='div'>
                    <RoomModelsOverlay />
                </Box>
                <Box component='div'>
                    {reactRouter && (
                        !isHideControlPanelandToolsBar && !isProjectLoading && <ControlPanel />
                    )}

                </Box>
                <Box component='div' sx={{
                    display: 'flex',
                    flexDirection: 'row',
                }}>
                    <RoomTypeView />
                </Box>
                {!isCreateRoomFlow &&(!isHideControlPanelandToolsBar &&<ToolsBar />) }
                {/* <div>
                    <ContextualMenu />
                </div> */}
                <SaveNotification />
                <BuilderNotification />
            </DndProvider>
            {(disableInteraction || (postedThreedGlbImageSignedUrl && createNewDesign)) && <Box
                ref={overlayRef}
                component='div'
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black
                    zIndex:createNewDesign?"": 9999, // Ensure it appears above everything (Menu paper uses 10000)
                    pointerEvents: 'auto', // Blocks underlying interaction
                    touchAction: 'none',
                    userSelect: 'none',
                }}
            >
                <Menu
                    id="account-menu"
                    open={true}
                    anchorReference="anchorPosition"
                    anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
                    disableAutoFocusItem
                    disableEnforceFocus
                    slotProps={{
                        paper: {
                            elevation: 0,
                            sx: {
                                zIndex: 10000,
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                width: '441px',
                                background: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '20px',
                                mt: 3,
                                transform: 'translate(-50%, -50%)',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: 'center', vertical: 'center' }}
                    anchorOrigin={{ horizontal: 'center', vertical: 'center' }}
                >

                    <MenuItem sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', whiteSpace: 'normal' }}>
                        {createNewDesign ? <>
                            <Typography sx={{ fontSize: '24px', fontWeight: '600', color: '#52575C' }}>Something to know:</Typography>
                            <Typography sx={{ fontSize: '16px' }}>Moving an object/product will create a</Typography>
                            <Typography sx={{ fontSize: '16px' }}>new template when generated</Typography>
                            <Stack spacing={2} direction="row" sx={{ margin: '20px 0' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    sx={{ margin: '0 10px', textTransform: 'capitalize' }}
                                    onClick={() => {
                                        dispatch(builderActions.updateProjectId(undefined))
                                        dispatch(builderActions.toggleCreateNewDesign(false))
                                        dispatch(builderActions.togglePostedThreedGlbImageSignedUrl(true))
                                        dispatch(saveProject({ currentRole }) as any)
                                    }}
                                >
                                    Got it
                                </Button>
                            </Stack>
                        </> : <>
                            <Typography sx={{ fontSize: '24px', fontWeight: '600', color: '#52575C' }}>Generating Preview...</Typography>
                            <Typography sx={{ fontSize: '16px' }}>Your room preview is being generated,</Typography>
                            <Typography sx={{ fontSize: '16px' }}>estimated time remaining: <Typography component="span" sx={{ fontSize: '12px', fontWeight: 'bold' }}>10 minutes</Typography></Typography>
                        </>}
                    </MenuItem>
                </Menu></Box>}
        </div >
    </>
    )
}

export default BuilderApp;